import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { COMPANY } from "../../lib/site";

const LINKS = [
  { label: "Catalog", href: "#catalog" },
  { label: "Shipping", href: "#shipping" },
  { label: "Why Us", href: "#why" },
  { label: "Contact", href: "#contact" },
];

export const Navbar = ({ onQuote }) => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 border-b transition-colors duration-300 ${
        scrolled ? "bg-background/85 backdrop-blur-xl border-border" : "bg-transparent border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 h-16 md:h-20 flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-3 group"
        >
          <span className="h-8 w-8 md:h-9 md:w-9 grid place-items-center bg-primary text-primary-foreground font-heading font-black text-lg">A</span>
          <span className="font-heading font-extrabold uppercase tracking-tight text-sm md:text-base leading-none">
            Angmish<span className="text-primary">.</span>
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-8">
          {LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => go(l.href)}
              className="font-mono text-xs uppercase tracking-[0.15em] text-muted-foreground hover:text-foreground transition-colors"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={COMPANY.phones[0].href}
            data-testid="nav-phone"
            className="hidden lg:flex items-center gap-2 font-mono text-xs text-muted-foreground hover:text-primary transition-colors"
          >
            <Phone size={14} /> {COMPANY.phones[0].value}
          </a>
          <button
            data-testid="nav-quote-btn"
            onClick={onQuote}
            className="hidden md:inline-flex items-center h-11 px-5 bg-primary text-primary-foreground font-mono text-xs uppercase tracking-[0.15em] font-semibold hover:bg-foreground transition-colors duration-200"
          >
            Request Quote
          </button>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className="md:hidden h-11 w-11 grid place-items-center border border-border"
            aria-label="Menu"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden border-t border-border bg-background"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {LINKS.map((l) => (
                <button
                  key={l.href}
                  onClick={() => go(l.href)}
                  className="text-left py-3 font-heading text-2xl font-bold uppercase tracking-tight hover:text-primary transition-colors"
                >
                  {l.label}
                </button>
              ))}
              <button
                onClick={() => { setOpen(false); onQuote(); }}
                className="mt-4 h-12 bg-primary text-primary-foreground font-mono text-xs uppercase tracking-[0.15em] font-semibold"
              >
                Request Quote
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
